import { useEffect, useState } from "react";
import api, { apiError, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import RichTextEditor from "@/components/RichTextEditor";
import { Switch } from "@/components/ui/switch";
import { ShieldCheck, DatabaseBackup, Download, RefreshCw, Clock, Palette, Check, ImageIcon, Upload, Trash2, Store, Phone, FileText } from "lucide-react";

const THEMES = [
  { id: "verde", name: "Verde Bosque", desc: "Identidad actual · verde, oliva y dorado", colors: ["#1F3D2E", "#6B7F4E", "#D4A017"] },
  { id: "turquesa", name: "Turquesa Industrial", desc: "Antracita, turquesa y naranja", colors: ["#0D1B2A", "#00BFA6", "#FF6A00"] },
];

const DEFAULT_CUSTOM = { primary: "#1F3D2E", accent: "#D4A017", cta: "#D4A017", surface: "#F3EDE2" };
const CUSTOM_FIELDS = [
  { key: "primary", label: "Primario", hint: "Header, footer y fondos oscuros" },
  { key: "accent", label: "Acento", hint: "Íconos, detalles y resaltados" },
  { key: "cta", label: "Botón CTA", hint: "Botón \"Solicitar cotización\"" },
  { key: "surface", label: "Superficie", hint: "Fondos claros de secciones" },
];

function ThemeSelector({ value, onChange, customTheme, onCustomChange }) {
  const ct = { ...DEFAULT_CUSTOM, ...(customTheme || {}) };
  return (
    <Card className="p-6 rounded-lg space-y-4" data-testid="theme-selector-card">
      <div>
        <div className="font-heading font-semibold flex items-center gap-2"><Palette className="w-4 h-4" /> Identidad visual del sitio público</div>
        <p className="text-xs text-muted-foreground mt-1">Elige la paleta de colores del sitio web público. El cambio se aplica de inmediato.</p>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        {THEMES.map((th) => {
          const active = value === th.id;
          return (
            <button key={th.id} type="button" onClick={() => onChange(th.id)} data-testid={`theme-option-${th.id}`}
              className={`text-left rounded-lg border-2 p-4 transition-all ${active ? "border-primary ring-2 ring-primary/20" : "border-border hover:border-primary/40"}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="font-heading font-semibold text-sm">{th.name}</span>
                {active && <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary"><Check className="w-3.5 h-3.5" /> Activo</span>}
              </div>
              <div className="flex gap-1.5 mb-3">
                {th.colors.map((c) => <span key={c} className="w-8 h-8 rounded-sm border border-black/10" style={{ background: c }} />)}
              </div>
              <p className="text-xs text-muted-foreground">{th.desc}</p>
            </button>
          );
        })}
        <button type="button" onClick={() => onChange("custom")} data-testid="theme-option-custom"
          className={`text-left rounded-lg border-2 p-4 transition-all ${value === "custom" ? "border-primary ring-2 ring-primary/20" : "border-border hover:border-primary/40"}`}>
          <div className="flex items-center justify-between mb-3">
            <span className="font-heading font-semibold text-sm">Personalizado</span>
            {value === "custom" && <span className="inline-flex items-center gap-1 text-xs font-semibold text-primary"><Check className="w-3.5 h-3.5" /> Activo</span>}
          </div>
          <div className="flex gap-1.5 mb-3">
            {[ct.primary, ct.accent, ct.cta].map((c, i) => <span key={i} className="w-8 h-8 rounded-sm border border-black/10" style={{ background: c }} />)}
          </div>
          <p className="text-xs text-muted-foreground">Define tu propia paleta de colores</p>
        </button>
      </div>

      {value === "custom" && (
        <div className="pt-2 border-t grid sm:grid-cols-2 gap-4" data-testid="custom-theme-pickers">
          {CUSTOM_FIELDS.map((f) => (
            <div key={f.key} className="flex items-center gap-3">
              <input type="color" value={ct[f.key]} onChange={(e) => onCustomChange(f.key, e.target.value)}
                className="w-11 h-11 rounded-md border cursor-pointer shrink-0 bg-transparent" data-testid={`custom-color-${f.key}`} />
              <div className="min-w-0">
                <div className="text-sm font-medium">{f.label} <span className="text-xs text-muted-foreground font-mono">{ct[f.key]}</span></div>
                <div className="text-xs text-muted-foreground truncate">{f.hint}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function HeroManager() {
  const [images, setImages] = useState([]);
  const [interval, setIntervalVal] = useState(7);
  const [transition, setTransition] = useState("crossfade");
  const [overlayActive, setOverlayActive] = useState(true);
  const [overlayIntensity, setOverlayIntensity] = useState("media");
  const [uploading, setUploading] = useState(false);
  useEffect(() => {
    api.get("/settings").then((r) => {
      setImages(r.data.hero_images || []);
      setIntervalVal(r.data.hero_interval || 7);
      setTransition(r.data.hero_transition || "crossfade");
      setOverlayActive(r.data.hero_overlay_active !== false);
      setOverlayIntensity(r.data.hero_overlay_intensity || "media");
    }).catch(() => {});
  }, []);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    let latest = images;
    try {
      for (const file of files) {
        if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name}: máx. 10 MB`); continue; }
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/hero-images", fd, { headers: { "Content-Type": "multipart/form-data" } });
        latest = data.hero_images;
        setImages(latest);
      }
      toast.success("Portada actualizada");
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const remove = async (id) => {
    try {
      const { data } = await api.delete(`/hero-images/${id}`);
      setImages(data.hero_images);
      toast.success("Imagen eliminada");
    } catch (err) { toast.error(apiError(err)); }
  };

  const changeInterval = async (v) => {
    const n = Number(v);
    setIntervalVal(n);
    try { await api.put("/settings", { hero_interval: n }); toast.success(`Rotación cada ${n} seg`); }
    catch (err) { toast.error(apiError(err)); }
  };

  const changeTransition = async (v) => {
    setTransition(v);
    try { await api.put("/settings", { hero_transition: v }); toast.success("Efecto de transición actualizado"); }
    catch (err) { toast.error(apiError(err)); }
  };

  const toggleOverlay = async (v) => {
    setOverlayActive(v);
    try { await api.put("/settings", { hero_overlay_active: v }); toast.success(v ? "Overlay activado" : "Overlay desactivado"); }
    catch (err) { toast.error(apiError(err)); }
  };

  const changeIntensity = async (v) => {
    setOverlayIntensity(v);
    try { await api.put("/settings", { hero_overlay_intensity: v }); toast.success("Intensidad del overlay actualizada"); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <Card className="p-6 rounded-lg space-y-4" data-testid="hero-manager-card">
      <div>
        <div className="font-heading font-semibold flex items-center gap-2"><ImageIcon className="w-4 h-4" /> Imágenes de portada del sitio</div>
        <p className="text-xs text-muted-foreground mt-1">Sube una o varias imágenes. Si hay más de una, el sitio las rota lentamente con un fundido. Sin imágenes, se usa la predeterminada.</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {images.map((img) => (
          <div key={img.id} className="relative group rounded-md overflow-hidden border aspect-video bg-muted" data-testid={`hero-img-${img.id}`}>
            <img src={img.url} alt="Portada" className="w-full h-full object-cover" />
            <button onClick={() => remove(img.id)} data-testid={`hero-remove-${img.id}`}
              className="absolute top-1.5 right-1.5 bg-red-600 text-white rounded-md p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        <label className="flex flex-col items-center justify-center gap-1.5 rounded-md border-2 border-dashed aspect-video cursor-pointer hover:border-primary/50 transition-colors text-muted-foreground" data-testid="hero-upload-label">
          {uploading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
          <span className="text-xs font-medium">{uploading ? "Subiendo..." : "Subir imagen"}</span>
          <input type="file" className="hidden" accept=".jpg,.jpeg,.png,.webp" multiple onChange={onFiles} data-testid="hero-file-input" />
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 pt-2 border-t">
        <div className="flex items-center gap-3">
          <Label className="text-sm">Efecto de transición</Label>
          <select value={transition} onChange={(e) => changeTransition(e.target.value)} data-testid="hero-transition-select"
            className="h-9 rounded-md border bg-background px-3 text-sm">
            <option value="fade">Desvanecer</option>
            <option value="crossfade">Fundido Cruzado</option>
            <option value="slide">Deslizamiento Lateral</option>
            <option value="kenburns">Efecto Ken Burns</option>
          </select>
        </div>
        {images.length > 1 && (
          <div className="flex items-center gap-3">
            <Label className="text-sm">Velocidad de rotación</Label>
            <select value={interval} onChange={(e) => changeInterval(e.target.value)} data-testid="hero-interval-select"
              className="h-9 rounded-md border bg-background px-3 text-sm">
              <option value={5}>5 segundos</option>
              <option value={7}>7 segundos</option>
              <option value={10}>10 segundos</option>
            </select>
          </div>
        )}
      </div>

      <div className="pt-3 border-t space-y-3" data-testid="hero-overlay-controls">
        <div className="flex items-center justify-between gap-4">
          <div>
            <Label className="text-sm">Capa oscura de legibilidad (overlay)</Label>
            <p className="text-xs text-muted-foreground mt-0.5">Oscurece la imagen para que el texto del hero se lea bien. Desactívala si tu foto ya es oscura.</p>
          </div>
          <Switch checked={overlayActive} onCheckedChange={toggleOverlay} data-testid="hero-overlay-toggle" />
        </div>
        {overlayActive && (
          <div className="flex items-center gap-3">
            <Label className="text-sm">Intensidad</Label>
            <select value={overlayIntensity} onChange={(e) => changeIntensity(e.target.value)} data-testid="hero-overlay-intensity"
              className="h-9 rounded-md border bg-background px-3 text-sm">
              <option value="suave">Suave</option>
              <option value="media">Media</option>
              <option value="fuerte">Fuerte</option>
            </select>
          </div>
        )}
      </div>
    </Card>
  );
}

function Backups() {
  const [list, setList] = useState([]);
  const [running, setRunning] = useState(false);
  const load = () => api.get("/backups").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setRunning(true);
    try { const r = await api.post("/backups/run"); toast.success(`Respaldo creado: ${r.data.name} (${r.data.docs} registros)`); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setRunning(false); }
  };
  const download = async (b) => {
    try {
      const r = await api.get(`/backups/${b.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `${b.name}.json.gz`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };
  const fmtSize = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${(n / 1024).toFixed(0)} KB`);

  return (
    <Card className="p-6 rounded-lg space-y-4" data-testid="backups-card">
      <div className="flex items-center justify-between gap-4">
        <div>
          <div className="font-heading font-semibold flex items-center gap-2"><DatabaseBackup className="w-4 h-4" /> Respaldos de base de datos</div>
          <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Respaldo automático diario a las 3:00 AM (hora de México) hacia la nube. Se conservan los últimos {14}.</p>
        </div>
        <Button onClick={runNow} disabled={running} data-testid="run-backup-now"><RefreshCw className={`w-4 h-4 mr-2 ${running ? "animate-spin" : ""}`} /> {running ? "Respaldando..." : "Respaldar ahora"}</Button>
      </div>
      <div className="divide-y border rounded-lg" data-testid="backups-list">
        {list.map((b) => (
          <div key={b.id} className="flex items-center justify-between gap-4 px-4 py-2.5 text-sm" data-testid="backup-row">
            <div>
              <div className="font-medium">{b.name}</div>
              <div className="text-xs text-muted-foreground">{fmtDateTime(b.created_at)} · {b.docs} registros · {b.collections} colecciones · {fmtSize(b.size)} · {b.trigger === "cron" ? "automático" : "manual"}</div>
            </div>
            <Button variant="outline" size="sm" onClick={() => download(b)} data-testid={`download-${b.id}`}><Download className="w-4 h-4 mr-1" /> Descargar</Button>
          </div>
        ))}
        {list.length === 0 && <div className="px-4 py-6 text-sm text-muted-foreground text-center">Aún no hay respaldos. El primero se creará automáticamente a las 3:00 AM o puedes generarlo ahora.</div>}
      </div>
    </Card>
  );
}

function BrandLogosManager() {
  const [logos, setLogos] = useState([]);
  const [uploading, setUploading] = useState(false);
  useEffect(() => {
    api.get("/settings").then((r) => setLogos(r.data.brand_logos || [])).catch(() => {});
  }, []);

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        if (file.size > 5 * 1024 * 1024) { toast.error(`${file.name}: máx. 5 MB`); continue; }
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/brand-logos", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setLogos(data.brand_logos);
      }
      toast.success("Logos actualizados");
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const remove = async (id) => {
    try {
      const { data } = await api.delete(`/brand-logos/${id}`);
      setLogos(data.brand_logos);
      toast.success("Logo eliminado");
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <Card className="p-6 rounded-lg space-y-4" data-testid="brand-logos-card">
      <div>
        <div className="font-heading font-semibold flex items-center gap-2"><Store className="w-4 h-4" /> Marcas y socios</div>
        <p className="text-xs text-muted-foreground mt-1">Sube los logos de marcas y socios (PNG, JPG, WEBP, SVG). Se muestran en el sitio en el orden en que los subes. Sin límite de cantidad.</p>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {logos.map((b) => (
          <div key={b.id} className="relative group rounded-md overflow-hidden border aspect-video bg-white grid place-items-center p-3" data-testid={`brand-logo-item-${b.id}`}>
            <img src={b.url} alt={b.name || "Logo"} className="max-h-full max-w-full object-contain" />
            <button onClick={() => remove(b.id)} data-testid={`brand-remove-${b.id}`}
              className="absolute top-1.5 right-1.5 bg-red-600 text-white rounded-md p-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        <label className="flex flex-col items-center justify-center gap-1.5 rounded-md border-2 border-dashed aspect-video cursor-pointer hover:border-primary/50 transition-colors text-muted-foreground" data-testid="brand-upload-label">
          {uploading ? <RefreshCw className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
          <span className="text-xs font-medium">{uploading ? "Subiendo..." : "Subir logo"}</span>
          <input type="file" className="hidden" accept=".jpg,.jpeg,.png,.webp,.svg,.gif" multiple onChange={onFiles} data-testid="brand-file-input" />
        </label>
      </div>
    </Card>
  );
}

function LegalDocs() {
  const [privacy, setPrivacy] = useState("");
  const [terms, setTerms] = useState("");
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    api.get("/settings").then((r) => { setPrivacy(r.data.privacy_html || ""); setTerms(r.data.terms_html || ""); setLoaded(true); }).catch(() => setLoaded(true));
  }, []);
  const save = async () => {
    try { await api.put("/settings", { privacy_html: privacy, terms_html: terms }); toast.success("Documentos legales actualizados"); }
    catch (e) { toast.error(apiError(e)); }
  };
  if (!loaded) return null;
  return (
    <Card className="p-6 rounded-lg space-y-4" data-testid="legal-docs-card">
      <div>
        <div className="font-heading font-semibold flex items-center gap-2"><FileText className="w-4 h-4" /> Aviso de privacidad y Términos</div>
        <p className="text-xs text-muted-foreground mt-1">Redacta el contenido legal. Se abrirá en una ventana flotante al hacer clic en “Aviso de privacidad y términos” en el pie del sitio web.</p>
      </div>
      <div>
        <Label className="mb-1.5 block">Aviso de Privacidad</Label>
        <RichTextEditor value={privacy} onChange={setPrivacy} minHeight={160} testid="privacy-editor" />
      </div>
      <div>
        <Label className="mb-1.5 block">Términos y Condiciones</Label>
        <RichTextEditor value={terms} onChange={setTerms} minHeight={160} testid="terms-editor" />
      </div>
      <Button onClick={save} data-testid="save-legal">Guardar documentos</Button>
    </Card>
  );
}

export default function Settings() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
  if (!s) return <div className="p-8 text-muted-foreground">Cargando...</div>;

  const save = async () => {
    try { await api.put("/settings", { min_margin_pct: Number(s.min_margin_pct), override_key: s.override_key, site_theme: s.site_theme || "verde" }); toast.success("Ajustes guardados"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const changeTheme = async (id) => {
    const payload = { site_theme: id };
    if (id === "custom") payload.custom_theme = s.custom_theme || { primary: "#1F3D2E", accent: "#D4A017", cta: "#D4A017", surface: "#F3EDE2" };
    setS({ ...s, site_theme: id, custom_theme: payload.custom_theme || s.custom_theme });
    try {
      await api.put("/settings", payload);
      localStorage.setItem("wk_site_theme", id);
      const name = id === "turquesa" ? "Turquesa Industrial" : id === "custom" ? "Personalizado" : "Verde Bosque";
      toast.success(`Tema del sitio: ${name}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const onCustomChange = async (key, val) => {
    const next = { ...(s.custom_theme || { primary: "#1F3D2E", accent: "#D4A017", cta: "#D4A017", surface: "#F3EDE2" }), [key]: val };
    setS({ ...s, custom_theme: next, site_theme: "custom" });
    try { await api.put("/settings", { site_theme: "custom", custom_theme: next }); }
    catch (e) { toast.error(apiError(e)); }
  };

  const saveContact = async () => {
    try { await api.put("/settings", { contact_email: s.contact_email || "ventas@west-kac.com", whatsapp_phone: s.whatsapp_phone || "" }); toast.success("Contacto actualizado"); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <div><h1 className="text-3xl font-heading font-bold tracking-tight">Ajustes Globales</h1><p className="text-muted-foreground text-sm">Reglas de control interno.</p></div>
      <Card className="p-6 rounded-lg space-y-5 max-w-xl">
        <div>
          <Label>Margen mínimo permitido (%)</Label>
          <Input type="number" value={s.min_margin_pct} onChange={(e) => setS({ ...s, min_margin_pct: e.target.value })} data-testid="min-margin-input" />
          <p className="text-xs text-muted-foreground mt-1">Cotizaciones por debajo de este margen requieren autorización.</p>
        </div>
        <div>
          <Label className="flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Clave de autorización de Administrador</Label>
          <Input value={s.override_key || ""} onChange={(e) => setS({ ...s, override_key: e.target.value })} data-testid="override-key-input" />
          <p className="text-xs text-muted-foreground mt-1">Se solicita al guardar cotizaciones con margen bajo.</p>
        </div>
        <Button onClick={save} data-testid="save-settings">Guardar ajustes</Button>
      </Card>

      <Card className="p-6 rounded-lg space-y-4 max-w-xl" data-testid="contact-settings-card">
        <div className="font-heading font-semibold flex items-center gap-2"><Phone className="w-4 h-4" /> Contacto del sitio web</div>
        <div>
          <Label>Correo de contacto</Label>
          <Input value={s.contact_email ?? "ventas@west-kac.com"} onChange={(e) => setS({ ...s, contact_email: e.target.value })} data-testid="contact-email-input" />
        </div>
        <div>
          <Label>WhatsApp (número con lada país)</Label>
          <Input value={s.whatsapp_phone ?? ""} onChange={(e) => setS({ ...s, whatsapp_phone: e.target.value })} placeholder="Ej. +52 1 55 1234 5678" data-testid="whatsapp-input" />
          <p className="text-xs text-muted-foreground mt-1">Se usa para el botón de WhatsApp del sitio público. Déjalo vacío para ocultarlo.</p>
        </div>
        <Button onClick={saveContact} data-testid="save-contact">Guardar contacto</Button>
      </Card>

      <LegalDocs />

      <ThemeSelector value={s.site_theme || "verde"} onChange={changeTheme} customTheme={s.custom_theme} onCustomChange={onCustomChange} />

      <HeroManager />

      <BrandLogosManager />

      <Backups />

      <Card className="p-6 rounded-lg text-sm text-muted-foreground max-w-xl">
        <div className="font-heading font-semibold text-foreground mb-2">Integración Microsoft</div>
        <p>El SSO de Microsoft Entra ID y el envío por Microsoft Graph están activos con las credenciales de Azure configuradas en el servidor.</p>
      </Card>
    </div>
  );
}
